/**
 * SMS FileShare HTTP adapter
 * Kullanıcının mevcut src/api/axiosConfig.js instance'ını kullanır.
 * Herkese açık indirme sayfası için izole bir Axios instance'ı kullanılır:
 * geçersiz paylaşım şifresi Portal oturumunu kapatamaz.
 */
import axios from 'axios';
import api from '../../../api/axiosConfig';

const originalBase = String(api.defaults.baseURL || '/api').replace(/\/+$/, '');
// Bazı geliştirme kurulumlarında baseURL localhost:8000, bazılarında localhost:8000/api.
const prefix = /\/api$/i.test(originalBase) ? '/fileshare' : '/api/fileshare';
const publicApi = axios.create({
  baseURL: originalBase,
  withCredentials: true,
  timeout: 30000,
});

export const publicFileBase = `${originalBase}${prefix}`;

const part = (value) => encodeURIComponent(String(value));

export function fileShareError(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join('; ');
  if (error?.code === 'ERR_NETWORK') return 'Sunucuya erişilemedi. API adresini ve CORS ayarlarını kontrol edin.';
  return error?.message || 'FileShare isteği başarısız oldu.';
}

async function send(client, method, endpoint, data, extra = {}) {
  try {
    const response = await client({
      method,
      url: `${prefix}${endpoint}`,
      ...(data === undefined ? {} : { data }),
      ...extra,
    });
    return response.data;
  } catch (e) {
    throw new Error(fileShareError(e));
  }
}

export const fileShareHttp = {
  settings: () => send(api, 'GET', '/settings'),
  list: () => send(api, 'GET', '/shares'),
  events: () => send(api, 'GET', '/events'),
  adminShares: () => send(api, 'GET', '/admin/shares'),
  adminEvents: () => send(api, 'GET', '/admin/events'),
  updateSettings: (settings) => send(api, 'PUT', '/admin/settings', settings),
  revoke: (id) => send(api, 'DELETE', `/shares/${part(id)}`),
  extend: (id, days) => send(api, 'PATCH', `/shares/${part(id)}/extend`, { days: Number(days) }),

  create: ({ files, title, note, days, customDate, password, downloadLimit, onUploadProgress }) => {
    const body = new FormData();
    files.forEach((file) => body.append('files', file, file.name));
    body.append('title', title || '');
    body.append('note', note || '');
    if (customDate) body.append('customDate', customDate);
    else if (days !== null && days !== undefined) body.append('days', String(days));
    if (password) body.append('password', password);
    if (downloadLimit !== '' && downloadLimit !== null && downloadLimit !== undefined) {
      body.append('downloadLimit', String(downloadLimit));
    }
    // multipart Content-Type eklemeyin. Browser boundary bilgisini kendi üretir.
    return send(api, 'POST', '/shares', body, {
      timeout: 0,
      ...(onUploadProgress ? { onUploadProgress } : {}),
    });
  },

  // JWT'siz, paylaşım token'ı ile çalışan uç noktalar; ayrı axios instance!
  publicInfo: (token) => send(publicApi, 'GET', `/public/${part(token)}`),
  verify: (token, password) => send(publicApi, 'POST', `/public/${part(token)}/verify`, { password }),
  downloadUrl: (token, fileId) => `${publicFileBase}/public/${part(token)}/files/${part(fileId)}/download`,
  zipUrl: (token) => `${publicFileBase}/public/${part(token)}/download-zip`,
};
